import React, { useState } from 'react';
import {
  MapPin,
  Phone,
  Mail,
  Clock,
  Calendar,
  CheckCircle,
  Navigation,
  Building,
  Filter,
} from 'lucide-react';
import { Card } from '@components/common/Card';
import { Badge } from '@components/common/Badge';
import { mockCenters } from '@mocks/bookings';

export const TestingCenters: React.FC = () => {
  const [filterState, setFilterState] = useState<string>('all');
  const [filterService, setFilterService] = useState<string>('all');

  // Get unique states from centers
  const states = ['all', ...Array.from(new Set(mockCenters.map((c) => c.address.split(',')[1]?.trim()).filter(Boolean)))];

  // Available services
  const services = [
    'all',
    'Biometric Capture',
    'Written Exams',
    'Medical Examination',
    'Document Verification',
    'Flight Tests',
  ];

  // Filter centers
  const filteredCenters = mockCenters.filter((center) => {
    const stateMatch = filterState === 'all' || center.address.includes(filterState);
    const serviceMatch = filterService === 'all'; // In real app, would check center.services
    return stateMatch && serviceMatch && center.is_active;
  });

  const stats = [
    { label: 'Total Centers', value: mockCenters.length, color: 'blue' },
    { label: 'Active Centers', value: mockCenters.filter((c) => c.is_active).length, color: 'green' },
    { label: 'States Covered', value: new Set(mockCenters.map((c) => c.address.split(',')[1]?.trim())).size, color: 'purple' },
    { label: 'Services Offered', value: services.length - 1, color: 'orange' },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-ncaa-primary to-blue-700 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Testing Centers</h1>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              Find NCAA-approved testing centers across Nigeria for your aviation examinations and assessments
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {stats.map((stat, index) => (
            <Card key={index}>
              <div className="text-center">
                <div className={`inline-flex items-center justify-center w-16 h-16 bg-${stat.color}-100 rounded-full mb-3`}>
                  <Building className={`h-8 w-8 text-${stat.color}-600`} />
                </div>
                <p className="text-3xl font-bold text-gray-900">{stat.value}</p>
                <p className="text-sm text-gray-600 mt-1">{stat.label}</p>
              </div>
            </Card>
          ))}
        </div>

        {/* Services Offered */}
        <Card className="mb-8">
          <h2 className="text-2xl font-bold text-ncaa-dark mb-6">Services Available at Testing Centers</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {services.slice(1).map((service, index) => (
              <div key={index} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
                <span className="text-gray-700 font-medium">{service}</span>
              </div>
            ))}
          </div>
        </Card>

        {/* Filters */}
        <Card className="mb-6">
          <div className="flex items-center gap-4">
            <Filter className="h-5 w-5 text-gray-400" />
            <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Filter by State</label>
                <select
                  value={filterState}
                  onChange={(e) => setFilterState(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ncaa-primary focus:border-transparent"
                >
                  {states.map((state) => (
                    <option key={state} value={state}>
                      {state === 'all' ? 'All States' : state}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Service</label>
                <select
                  value={filterService}
                  onChange={(e) => setFilterService(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ncaa-primary focus:border-transparent"
                >
                  {services.map((service) => (
                    <option key={service} value={service}>
                      {service === 'all' ? 'All Services' : service}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </Card>

        {/* Results Count */}
        <div className="mb-6">
          <p className="text-gray-600">
            Showing <span className="font-semibold text-gray-900">{filteredCenters.length}</span> testing center{filteredCenters.length !== 1 ? 's' : ''}
          </p>
        </div>

        {/* Centers List */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {filteredCenters.map((center) => (
            <Card key={center.id} className="hover:shadow-lg transition-shadow">
              <div className="space-y-4">
                {/* Header */}
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{center.name}</h3>
                    <Badge variant="success" icon={<CheckCircle className="h-3 w-3" />}>
                      Active
                    </Badge>
                  </div>
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Building className="h-6 w-6 text-blue-600" />
                  </div>
                </div>

                {/* Details */}
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <MapPin className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-gray-700">Address</p>
                      <p className="text-sm text-gray-600">{center.address}</p>
                    </div>
                  </div>

                  {center.phone && (
                    <div className="flex items-start gap-3">
                      <Phone className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-gray-700">Phone</p>
                        <a href={`tel:${center.phone}`} className="text-sm text-blue-600 hover:underline">
                          {center.phone}
                        </a>
                      </div>
                    </div>
                  )}

                  {center.email && (
                    <div className="flex items-start gap-3">
                      <Mail className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-gray-700">Email</p>
                        <a href={`mailto:${center.email}`} className="text-sm text-blue-600 hover:underline">
                          {center.email}
                        </a>
                      </div>
                    </div>
                  )}

                  <div className="flex items-start gap-3">
                    <Clock className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-gray-700">Operating Hours</p>
                      <p className="text-sm text-gray-600">Monday - Friday: 8:00 AM - 5:00 PM</p>
                      <p className="text-sm text-gray-600">Saturday: 9:00 AM - 2:00 PM</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <Calendar className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-gray-700">Capacity</p>
                      <p className="text-sm text-gray-600">{center.capacity} appointments per day</p>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="pt-4 border-t border-gray-200 flex gap-3">
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(center.address)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <Navigation className="h-4 w-4" />
                    Get Directions
                  </a>
                  <a
                    href="/register"
                    className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 border-2 border-ncaa-primary text-ncaa-primary font-medium rounded-lg hover:bg-ncaa-primary hover:text-white transition-colors"
                  >
                    <Calendar className="h-4 w-4" />
                    Book Appointment
                  </a>
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* Empty State */}
        {filteredCenters.length === 0 && (
          <Card>
            <div className="text-center py-16">
              <MapPin className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No Centers Found</h3>
              <p className="text-gray-600 mb-6">Try adjusting your filters to see more results</p>
              <button
                onClick={() => {
                  setFilterState('all');
                  setFilterService('all');
                }}
                className="inline-flex items-center justify-center px-6 py-2 bg-ncaa-primary text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
              >
                Clear Filters
              </button>
            </div>
          </Card>
        )}

        {/* Important Information */}
        <Card className="bg-blue-50 border-blue-200">
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Important Information</h3>
            <ul className="space-y-2 text-gray-700">
              <li className="flex items-start gap-2">
                <CheckCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <span>All appointments must be booked through the DCEV Portal</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <span>Arrive at least 15 minutes before your scheduled appointment</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <span>Bring original copies of all required documents and valid ID</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <span>Face masks and hand sanitization may be required at testing centers</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <span>Contact the center directly if you need to reschedule or have questions</span>
              </li>
            </ul>
          </div>
        </Card>

        {/* CTA */}
        <Card className="bg-gradient-to-r from-ncaa-primary to-blue-600 text-white mt-8">
          <div className="text-center py-8">
            <h2 className="text-3xl font-bold mb-4">Ready to Book Your Appointment?</h2>
            <p className="text-xl text-blue-100 mb-6">
              Create an account and schedule your test at any of our approved centers
            </p>
            <a
              href="/register"
              className="inline-flex items-center justify-center px-8 py-3 bg-white text-ncaa-primary font-semibold rounded-lg hover:bg-gray-100 transition-colors"
            >
              Get Started
            </a>
          </div>
        </Card>
      </div>
    </div>
  );
};
